export type NoticeType =
  | 'approval'
  | 'courseEvalInvite'
  | 'review_plan_assigned'
  | 'review_plan_cancelled'
  | 'review_plan_focus'
  | 'unknown';

export interface NoticePayload {
  content: string;
  extra: Record<string, unknown>;
  noticeType: NoticeType;
  noticeURL?: string;
  rawNoticeType?: string;
  schemaVersion: 0 | 1;
  senderName?: string;
  title: string;
  traceID?: string;
}

const knownNoticeTypes = new Set<NoticeType>([
  'approval',
  'courseEvalInvite',
  'review_plan_assigned',
  'review_plan_cancelled',
  'review_plan_focus',
]);

/** isRecord 判断未知输入是否为可安全读取的普通对象。 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** readString 只读取非空字符串，避免把对象隐式转换为可执行字段。 */
export function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** parseJSONRecord 尝试把对象或 JSON 字符串转换为对象，解析失败时安全返回空值。 */
function parseJSONRecord(value: unknown): Record<string, unknown> | undefined {
  if (isRecord(value)) return value;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** unwrapNoticeRecord 兼容 OpenIM customElem、历史 data/detail 包装和直接 Notice JSON。 */
function unwrapNoticeRecord(value: unknown): Record<string, unknown> | undefined {
  let current = parseJSONRecord(value);
  for (let depth = 0; current && depth < 4; depth += 1) {
    if (
      'noticeType' in current
      || 'notice_type' in current
      || 'title' in current
      || 'noticeURL' in current
    ) return current;
    const nested = current.data ?? current.detail ?? current.extension;
    const parsed = parseJSONRecord(nested);
    if (!parsed) return current;
    current = parsed;
  }
  return current;
}

/** parseNoticePayload 将 v1 和无版本历史通知收敛为不会抛错的只读展示模型。 */
export function parseNoticePayload(value: unknown): NoticePayload {
  const record = unwrapNoticeRecord(value) ?? {};
  const rawNoticeType = readString(record.noticeType ?? record.notice_type ?? record.type);
  const noticeType = rawNoticeType && knownNoticeTypes.has(rawNoticeType as NoticeType)
    ? rawNoticeType as NoticeType
    : 'unknown';
  const extra = parseJSONRecord(record.extra) ?? {};
  const schemaVersion = Number(record.schemaVersion ?? record.schema_version) === 1 ? 1 : 0;
  return {
    content: readString(record.content ?? record.description ?? record.message) ?? '',
    extra,
    noticeType,
    noticeURL: readString(record.noticeURL ?? record.noticeUrl ?? record.url),
    rawNoticeType,
    schemaVersion,
    senderName: readString(record.senderName ?? record.sender_name),
    title: readString(record.title) ?? '系统通知',
    traceID: readString(record.traceID ?? record.traceId ?? record.trace_id),
  };
}
